// imports
import clsx from 'clsx';
import toastr from 'toastr';
import { AnyAction } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import 'react-circular-progressbar/dist/styles.css';
import { useSelector, useDispatch } from 'react-redux';
import React, {
  useRef, useState, useEffect, DragEvent
} from 'react';
import { buildStyles, CircularProgressbarWithChildren } from 'react-circular-progressbar';

import EmptyQuest from './emptyQuest';
import { ICON, IMAGE } from '../../../../assets';
import { LINKS, ROUTES } from '../../../../constants';
import PaperBG from '../../../../atoms/background/paper';
import InfoTooltip from '../../../../atoms/tooltip/info';
import PrimaryButton from '../../../../atoms/button/primary';
import { HeroNFT } from '../../../../repositories/web3/types';
import { DATE_UTILS, QUEST_UTILS } from '../../../../common/utils';
import TraitDetailTooltip from '../../../../atoms/tooltip/traitDetail';
import {
  ITraitMatching, Quest, Region,
  QuestCost, QuestStatus, QuestType
} from '../../../../common/types';
import {
  selectConfig,
  selectUIQuestProgress,
  selectUserHeroes, selectUserQuests, selectUserPreferences, selectUserDelegateWallet
} from '../../../../repositories/store/slices';
import { startQuest, claimRewards } from '../../../../repositories/store/thunks';

// interfaces / types / consts
const QUEST_SLOTS = 3;
const REWARD_COLORS = {
  darkGreen: '#3A860B',
  lightGreen: '#96C537',
  orange: '#FDAB30',
  red: '#F15757'
};
interface QuestProps {
  crew: number[];
  quest?: Quest;
  currentSlot?: number;
  placeholder?: Region;
  insufficientResources?: QuestCost[];
  questTraitMatching?: ITraitMatching[][];
  setSlot?: (slot: number) => void;
  heroDraggedCB?: (hero?: HeroNFT) => void;
  heroRemovedCB?: (slotNumber: number) => void;
  onDroppedCB?: (e: DragEvent<HTMLDivElement>, slot: number) => void;
}
interface ActionBtnProps {
  text: string;
  disabled: boolean;
  tooltipTitle: string;
  tooltipText: string | JSX.Element;
  action: () => void;
}

// exports
export default function QuestDetailView(props: QuestProps): JSX.Element {
  const {
    crew, quest, placeholder, currentSlot,
    insufficientResources, questTraitMatching,
    onDroppedCB, heroRemovedCB, heroDraggedCB, setSlot
  } = props;

  // hooks configuration
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const prevStartId = useRef<string>();
  const prevClaimId = useRef<string>();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const questsProgress = useSelector(selectUIQuestProgress);
  const { world, items, texts } = useSelector(selectConfig);
  const [captchaCompleted, setCaptchaCompleted] = useState(false);
  const {
    startId, claimId, startError, claimError
  } = useSelector(selectUserQuests);
  const heroes = useSelector(selectUserHeroes);
  const { showCaptcha } = useSelector(selectUserPreferences);
  const { wallet: delegateWallet } = useSelector(selectUserDelegateWallet);

  // private methods
  const onCaptchaCompleted = (): void => setCaptchaCompleted(true);
  const handleSlotChange = (slotIdx: number): void => { if (setSlot) setSlot(slotIdx); };
  const handleSelectHero = (slotIdx: number): void => { navigate(ROUTES.QUEST_CREW.replace(':id', quest?.id || ''), { state: { quest, slotIdx } }); };
  const handleClaimRewards = (): void => {
    dispatch(claimRewards({
      heroTokens: crew,
      questId: quest!.id,
      pinId: quest!.pinId,
      delegateWallet: delegateWallet!,
      captchaResponse: recaptchaRef.current?.getValue() || ''
    }) as unknown as AnyAction);
  };
  let rewardChances = Array.from({ length: quest?.rewards.length || 0 }, () => 0);
  const crewHasHeroes = quest && Boolean(crew.filter((heroId) => heroId).length);
  let sortedQuestTraits: ITraitMatching[][] = [];
  if (questTraitMatching?.length) {
    sortedQuestTraits = questTraitMatching;
  } else if (quest) {
    sortedQuestTraits = quest?.traits.map((traits) => traits.map((trait) => ({ id: trait, icon: trait.toUpperCase(), status: 'idle' })));
  }
  if (crewHasHeroes) { rewardChances = quest.rewardChances || QUEST_UTILS.getRewardChances(quest, sortedQuestTraits); }
  const handleQuestStart = (): void => {
    dispatch(startQuest({
      rewardChances,
      heroTokens: crew,
      questId: quest!.id,
      pinId: quest!.pinId,
      delegateWallet: delegateWallet!,
      crewTraitMatching: questTraitMatching || [],
      isCampaign: quest!.type === QuestType.Campaign,
      captchaResponse: recaptchaRef.current?.getValue() || ''
    }) as unknown as AnyAction);
  };

  // state variables
  const rewardClaimInProgress = Boolean(claimId);
  const costMet = !insufficientResources?.length;
  const inProgress = claimId === quest?.id || startId === quest?.id;
  const pinName = (quest?.pinId && world.pins && world.pins[quest!.pinId]) ? world.islands[world.pins[quest!.pinId].islandId].name : '';
  if (!startId && prevStartId.current && prevStartId.current === quest?.id && startError) {
    toastr.error(startError, 'Could not start the quest', { toastClass: 'bg-danger' });
  }
  if (!claimId && prevClaimId.current && prevClaimId.current === quest?.id && claimError) {
    toastr.error(claimError, 'Could not claim rewards', { toastClass: 'bg-danger' });
  }
  let actionBtnProps: ActionBtnProps = {
    disabled: !costMet,
    text: 'Select Hero',
    tooltipTitle: inProgress ? texts?.tooltip_quest_starting?.title : texts?.tooltip_insufficient_resources?.title,
    tooltipText: inProgress ? texts?.tooltip_quest_starting?.body : texts?.tooltip_insufficient_resources?.body,
    action: (): void => { navigate(ROUTES.QUEST_CREW.replace(':id', quest?.id || ''), { state: { quest } }); }
  };
  if (quest?.status !== QuestStatus.available) {
    actionBtnProps = {
      text: 'Claim Rewards',
      disabled: inProgress || rewardClaimInProgress || quest?.status === QuestStatus.inProgress,
      tooltipTitle: inProgress || rewardClaimInProgress ? texts?.tooltip_quest_rewards_claiming?.title : texts?.tooltip_quest_rewards_claim?.title,
      tooltipText: inProgress || rewardClaimInProgress ? texts?.tooltip_quest_rewards_claiming?.body : texts?.tooltip_quest_rewards_claim?.body,
      action: handleClaimRewards
    };
  } else if (onDroppedCB) {
    actionBtnProps = {
      ...actionBtnProps,
      text: 'Start',
      disabled: inProgress || (!crewHasHeroes || !costMet),
      tooltipTitle: crewHasHeroes ? actionBtnProps.tooltipTitle : texts?.tooltip_quest_crew_selection?.title,
      tooltipText: crewHasHeroes ? actionBtnProps.tooltipText : texts?.tooltip_quest_crew_selection?.body,
      action: handleQuestStart
    };
  }
  const shouldShowCaptcha = showCaptcha && (Boolean(onDroppedCB) || quest?.status === QuestStatus.completed) && !actionBtnProps.disabled;
  useEffect(() => { // componentDidMount
    if (!shouldShowCaptcha) { setCaptchaCompleted(false); }
  }, [shouldShowCaptcha]);
  useEffect(() => {
    prevStartId.current = startId;
    prevClaimId.current = claimId;
  });

  // render
  return (
    <div className="dv-container quest">
      <PaperBG fold />
      {quest ? (
        <div className="dv-content">
          <div className="dv-title">
            <div className="heading">
              <img src={IMAGE.HERO_TITLE_MOTIF} alt="Left Motif" />
              <span>{quest.name}</span>
              <img className="right-motif" src={IMAGE.HERO_TITLE_MOTIF} alt="Right Motif" />
            </div>
            <div className="type region">{pinName}</div>
          </div>
          <div className="scrollable-section">
            <div className="main-section">
              <p className="quest-description">{quest.description}</p>
              <div className="quest-duration-team-outer">

                {(quest.status === QuestStatus.inProgress && questsProgress[quest.id]?.time === '0s') ? (<div />)
                  : (
                    <div className="quest-duration">
                      <img src={ICON.QUEST_DURATION} alt="Duration Icon" />
                      <span className={clsx(quest.status === QuestStatus.completed && 'completed')}>
                        {quest.status === QuestStatus.completed && 'Completed'}
                        {quest.status === QuestStatus.available
                          && `Duration: ${DATE_UTILS.formatTime(quest.duration * 1000)}`}
                        {quest.status === QuestStatus.inProgress && `Remaining: ${questsProgress[quest.id]?.time}`}
                      </span>
                    </div>
                  )}
                <div className="quest-team">
                  <div className="title">Adventure Party</div>
                  <button type="button" className="info-btn adventure-party">
                    <img src={ICON.QUEST_SMALL_INFO} alt="info" />
                  </button>
                  <InfoTooltip
                    anchorSelect=".adventure-party"
                    title={texts?.tooltip_quest_crew_info?.title}
                    description={texts?.tooltip_quest_crew_info?.body}
                  />
                  <div className="hero-slot-container">
                    {Array.from({ length: QUEST_SLOTS }).map((_, idx) => {
                      const isHeroSelected = Boolean(crew[idx] && heroes.fetchStatus === 'success');
                      const key = `crew-${idx}`;
                      const heroSelectedClasses = `${idx % 2 === 0 ? 'rotate-even' : 'rotate-odd'} filled bottom-shadow`;
                      const hero = heroes.nfts.find((heroNFT) => heroNFT.tokenId === crew[idx]);
                      return (
                        <div
                          key={key}
                          className="hero-slot"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { if (onDroppedCB) { onDroppedCB(e, idx); } }}
                        >
                          <div className={clsx(
                            'btn-hero',
                            isHeroSelected && heroSelectedClasses,
                            Boolean(onDroppedCB) && currentSlot === idx ? 'selected' : 'unselected'
                          )}
                          >
                            {isHeroSelected
                              ? (
                                <img
                                  alt="hero_img"
                                  className="hero-img"
                                  src={hero?.image || IMAGE.HERO_PLACEHOLDER}
                                  onDragStart={() => { if (heroDraggedCB) { heroDraggedCB(hero); } }}
                                  onDragEnd={() => { if (heroRemovedCB && !startId) heroRemovedCB(idx); }}
                                  onError={({ currentTarget }) => { currentTarget.src = IMAGE.HERO_PLACEHOLDER; }}
                                />
                              )
                              : (
                                <button
                                  type="button"
                                  className="add-hero"
                                  disabled={!costMet || quest.status !== QuestStatus.available}
                                  onClick={onDroppedCB ? () => handleSlotChange(idx) : () => handleSelectHero(idx)}
                                >
                                  <img draggable={false} src={ICON.QUEST_ADD_HERO} alt="add_hero" />
                                </button>
                              )}
                            {(isHeroSelected && !quest.startTime)
                              && (
                                <button
                                  type="button"
                                  className="remove-btn"
                                  disabled={Boolean(startId) && startId === quest.id}
                                  onClick={() => { if (heroRemovedCB) heroRemovedCB(idx); }}
                                >
                                  <img src={ICON.QUEST_REMOVE_HERO} alt="leader" />
                                </button>
                              )}
                          </div>
                          <div className="traits">
                            {sortedQuestTraits[idx].map((traitMatching) => {
                              const { id, icon, status } = traitMatching;
                              return (
                                <div key={id} className={`trait-block ${status}`}>
                                  <img id={traitMatching.id} src={ICON[icon]} alt="icon" />
                                  <TraitDetailTooltip openOnClick anchorSelect={`#${traitMatching.id}`} traitId={id || ''} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
              <div className="quest-rewards">
                <span>Potential Rewards</span>
                <div className="reward-container">
                  {quest.rewards?.map((reward, index) => {
                    const key = `reward_${index}_${reward.itemId}`;
                    const rewardChance = rewardChances[index];
                    let pathColor;
                    if (rewardChance >= 70) {
                      pathColor = REWARD_COLORS.darkGreen;
                    } else if (rewardChance >= 50) {
                      pathColor = REWARD_COLORS.lightGreen;
                    } else if (rewardChance >= 10) {
                      pathColor = REWARD_COLORS.orange;
                    } else { pathColor = REWARD_COLORS.red; }
                    return (
                      <div key={key} className="reward">
                        <CircularProgressbarWithChildren
                          value={rewardChance}
                          strokeWidth={7}
                          circleRatio={0.75}
                          styles={buildStyles({
                            pathColor,
                            trailColor: '#C0A57D',
                            strokeLinecap: 'round',
                            rotation: 1 / 2 + 1 / 8,
                            pathTransitionDuration: 2
                          })}
                        >
                          <div className="reward-amount">{reward.amount}</div>
                          <img
                            alt="reward"
                            id={`reward-image-${key}`}
                            src={`${LINKS.BACKPACK_BUCKET_URL}/${reward.itemId}.png`}
                            onError={({ currentTarget }) => { currentTarget.src = IMAGE.ITEM_PLACEHOLDER; }}
                          />
                          <InfoTooltip
                            title={items[reward.itemId]?.name}
                            description={items[reward.itemId]?.info}
                            anchorSelect={`#reward-image-${key}`}
                          />
                        </CircularProgressbarWithChildren>
                        <div className="reward-chance">
                          <div className="percent-text">{`${rewardChance}%`}</div>
                          <div className="chance-text">Chance</div>
                        </div>
                      </div>
                    );
                  })}
                  <button type="button" className="info-btn rewards-info">
                    <img src={ICON.QUEST_SMALL_INFO} alt="info" />
                  </button>
                  <InfoTooltip
                    anchorSelect=".rewards-info"
                    title={texts?.tooltip_quest_rewards_info?.title}
                    description={texts?.tooltip_quest_rewards_info?.body}
                  />
                </div>
              </div>
              {Boolean(quest.cost.length) && (
                <div className={clsx('quest-costs', insufficientResources?.length && 'insufficient')}>
                  <span>Start Quest Cost:</span>
                  <div className="cost-container">
                    {quest.cost?.map((cost) => (
                      <div key={cost.itemId} className={clsx('cost', cost.itemId, insufficientResources?.includes(cost) && 'insufficient-cost')}>
                        <img
                          alt="cost"
                          src={`${LINKS.BACKPACK_BUCKET_URL}/${cost.itemId}.png`}
                          onError={({ currentTarget }) => { currentTarget.src = IMAGE.ITEM_PLACEHOLDER; }}
                        />
                        {cost.amount}
                        {insufficientResources?.includes(cost) ? (
                          <InfoTooltip
                            title="Insufficient Resource"
                            anchorSelect={`.${cost.itemId}`}
                            description={`You need ${items[cost.itemId]?.name} x${cost.amount}`}
                          />
                        ) : (
                          <InfoTooltip anchorSelect={`.${cost.itemId}`} title={items[cost.itemId]?.name} description={items[cost.itemId]?.info} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {shouldShowCaptcha
            && (
              <div className="recaptcha">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_CLIENT_SECRET!}
                  onChange={onCaptchaCompleted}
                />
              </div>
            )}
          <div className="action-btn-container">
            <PrimaryButton
              isLoading={inProgress}
              textElemId="action-btn"
              text={actionBtnProps.text}
              className="success with-spinner"
              disabled={actionBtnProps.disabled || (shouldShowCaptcha && !captchaCompleted)}
              onClick={actionBtnProps.action}
            />
            {actionBtnProps.disabled
              && (<InfoTooltip anchorSelect="#action-btn" title={actionBtnProps.tooltipTitle} description={actionBtnProps.tooltipText} />)}
          </div>
        </div>
      ) : <EmptyQuest placeholder={placeholder || { name: '', description: '' }} />}
    </div>
  );
}

// default props
QuestDetailView.defaultProps = {
  currentSlot: 0,
  placeholder: {},
  quest: undefined,
  setSlot: undefined,
  questTraitMatching: [],
  insufficientResources: [],
  onDroppedCB: undefined,
  heroDraggedCB: undefined,
  heroRemovedCB: undefined
};
